import { Box, IconButton, Typography } from '@mui/material';
import { useRef, useState } from 'react';

import { ReactComponent as ArrowUpIcon } from 'assets/arrow-up-white.svg';
import { ReactComponent as ArrowUpDisabledIcon } from 'assets/arrow-up.svg';

import { useInput } from 'customHook/useInput';
import { GetNotesResponseType } from 'model/tasks.model';
import zeplinColor from 'theme/Color';
import CustomButton from '../CustomButton/CustomButton';
import CustomTextField from '../CustomTextField/CustomTextField';
import CustomNoteCard from './CustomNoteCard';
import {
  cancelEditButton,
  emptyNotesContainer,
  inputContainer,
  notesContainer,
  notesHeader,
  notesListContainer,
  submitButton,
  textField,
} from './styles';
import styles from './styles.module.css';
import { Props } from './type';

function CustomNotes<T extends GetNotesResponseType>({ manager }: Props<T>) {
  const [noteId, setSelectedNoteId] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { notes, loading, deleteNote, updateNote, addNote } = manager;

  const {
    handleChange: handleNotesChange,
    handleBlur: handleNotesBlur,
    value: note,
    setValue: setNote,
  } = useInput('', () => {
    return false;
  });

  const isSubmitDisabled = note === '';

  const handleNoteSubmit = () => {
    noteId ? updateNote(noteId, note) : addNote(note);
    setNote('');
    setSelectedNoteId('');
  };

  const handleCancelEdit = () => {
    setNote('');
    setSelectedNoteId('');
  };

  return (
    <Box sx={notesContainer}>
      <Box sx={notesHeader}>
        <img src={'/assets/note.png'} width={'20px'} height={'20px'} alt='note icon' />
        <Typography variant='responsiveBody2' color={zeplinColor.Neutral8}>
          Notes
        </Typography>
      </Box>
      {notes?.length || loading ? (
        <Box sx={notesListContainer}>
          {notes?.map((val) => (
            <CustomNoteCard
              data={val}
              setNotes={setNote}
              setSelectedNoteId={setSelectedNoteId}
              deleteNotes={deleteNote}
              loading={loading}
              key={val?.id}
            />
          ))}
        </Box>
      ) : (
        <Box sx={emptyNotesContainer}>
          {' '}
          <img
            src={'/assets/no-notes.png'}
            className={styles['no-notes-icon']}
            alt='no notes icon'
          />
          <Typography
            variant='responsiveHeading3'
            color={zeplinColor.BasicBlack}
            sx={{ marginTop: { xs: '12px', xl: '24px' } }}
          >
            No Notes Yet
          </Typography>
          <Typography
            variant='responsiveBody3'
            color={zeplinColor.Neutral8}
            sx={{ width: '70%', textAlign: 'center', marginTop: { xs: '6px', xl: '12px' } }}
          >
            Add notes to ensure no detail gets missed.
          </Typography>
        </Box>
      )}

      <Box sx={{ ...inputContainer, backgroundColor: zeplinColor.Neutral1 }}>
        <Box sx={{ ...cancelEditButton }}>
          <CustomButton
            onClick={handleCancelEdit}
            disabled={isSubmitDisabled}
            text='Cancel Edit'
            variant='text'
            style={{
              display: !noteId ? 'none' : 'block',
            }}
          />
        </Box>
        <CustomTextField
          ref={inputRef}
          style={{
            ...textField,
            '& .MuiOutlinedInput-notchedOutline': { border: `1px solid ${zeplinColor.Neutral5}` },
          }}
          onBlur={handleNotesBlur}
          onChange={handleNotesChange}
          onEnter={isSubmitDisabled ? () => {} : () => handleNoteSubmit()}
          name='notes'
          text={note}
          error={''}
          label={noteId ? 'Edit a note' : 'Add a note'}
          type='notes'
          tabindex={1}
          endIcon={
            <IconButton
              onClick={() => handleNoteSubmit()}
              disabled={isSubmitDisabled}
              sx={{
                ...submitButton,
                bgcolor: zeplinColor.Brand1,
                ':disabled': { bgcolor: zeplinColor.Neutral5 },
                ':hover': { bgcolor: zeplinColor.Brand1Hover },
                ':active': { bgcolor: zeplinColor.Brand1Active },
              }}
            >
              {!note ? <ArrowUpDisabledIcon /> : <ArrowUpIcon />}
            </IconButton>
          }
        />
      </Box>
    </Box>
  );
}

export default CustomNotes;
